import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from "../../../axiosConfig"; // Added axios import

const DestinationImage = ({ destinationUuid, setActiveTab, activeTab, isViewMode, isEditMode }) => {
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [blocks, setBlocks] = useState([
    { id: 1, name: "Block 1", images: [], aspectWidth: 16, aspectHeight: 9, exampleResolution: "1920x1080, 1600x900, 1280x720" },
    { id: 2, name: "Block 2", images: [], aspectWidth: 16, aspectHeight: 9, exampleResolution: "1920x1080, 1600x900, 1280x720" },
    { id: 3, name: "Block 3", images: [], aspectWidth: 3, aspectHeight: 4, exampleResolution: "900x1200, 768x1024, 600x800" },
    { id: 4, name: "Block 4", images: [], aspectWidth: 9, aspectHeight: 16, exampleResolution: "1080x1920, 900x1600, 720x1280" },
    { id: 5, name: "Block 5", images: [], aspectWidth: 9, aspectHeight: 16, exampleResolution: "1080x1920, 900x1600, 720x1280" },
    { id: 6, name: "Block 6", images: [], aspectWidth: 16, aspectHeight: 9, exampleResolution: "1920x1080, 1600x900, 1280x720" }
  ]);

  // Create axios instance with base URL and headers if needed


  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const fileInputRefs = useRef({});

  // Scroll to top when component loads
  useEffect(() => {
    if (activeTab === 'images') {
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
    
    // Fetch saved images if destinationUuid exists
    const fetchDestinationImages = async () => {
      if (destinationUuid && activeTab === 'images') {
        try {
          const response = await axiosInstance.get(
            `/internal-api/v1/client-web/destination-panel/config/${destinationUuid}/view`
          );
          if (response.data && response.data.result && response.data.result.images) {
            const savedImages = response.data.result.images;
            const localImageServer = response.data.result.localImageServer || '';
            
            const imagesByRatio = savedImages.reduce((acc, img) => {
              const key = `${img.image?.aspWidth}x${img.image?.aspHeight}`;
              if (!acc[key]) acc[key] = [];
              acc[key].push(img);
              return acc;
            }, {});

            Object.values(imagesByRatio).forEach(list => {
              list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            });

            const updatedBlocks = blocks.map(block => {
              const key = `${block.aspectWidth}x${block.aspectHeight}`;
              const pool = imagesByRatio[key] || [];

              if (pool.length > 0) {
                const img = pool.shift();
                return {
                  ...block,
                  images: [
                    {
                      id: img.id,
                      name: img.image?.fullImageName || '',
                      imageUrl: localImageServer + img.image?.path,
                      validityFrom: img.validFrom?.split(' ')[0] || '',
                      file: null
                    }
                  ]
                };
              }

              return block;
            });
            
            setBlocks(updatedBlocks);
            setIsReadOnly(isViewMode);
          }
        } catch (error) {
          console.error("Error fetching destination images:", error);
        }
      }
    };
    
    fetchDestinationImages();
  }, [activeTab, destinationUuid, isViewMode]);

  useEffect(() => {
    if (isViewMode) {
      setIsReadOnly(true);
    }
  }, [isViewMode]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleImageDelete = (blockId, imageId) => {
    setBlocks(blocks.map(block => {
      if (block.id === blockId) {
        return {
          ...block,
          images: block.images.filter(img => img.id !== imageId)
        };
      }
      return block;
    }));
  };

  const handleUploadClick = (blockId) => {
    if (fileInputRefs.current[blockId]) {
      fileInputRefs.current[blockId].click();
    }
  };

  const handleFileChange = (e, blockId) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newImages = files.map(file => {
      const id = Date.now() + Math.random().toString(36).substr(2, 5);
      return {
        id,
        name: file.name,
        file,
        validityFrom: ''
      };
    });

    setBlocks(blocks.map(block => {
      if (block.id === blockId) {
        return {
          ...block,
          images: [...block.images, ...newImages]
        };
      }
      return block;
    }));

    // Reset the file input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleImageValidityChange = (blockId, imageId, field, value) => {
    setBlocks(blocks.map(block => {
      if (block.id === blockId) {
        return {
          ...block,
          images: block.images.map(img => {
            if (img.id === imageId) {
              return {
                ...img,
                [field]: value
              };
            }
            return img;
          })
        };
      }
      return block;
    }));
  };

  const handlePreviousClick = () => {
    setActiveTab('location');
  };

  const handleSaveClick = async () => {
    // Validate all blocks with images
    let hasError = false;
    let message = '';

    const uploadableImages = blocks.flatMap(block =>
      block.images
        .filter(image => image.file)
        .map(image => ({ block, image }))
    );

    const imagesToValidate = isEditMode ? uploadableImages.map(item => item.image) : blocks.flatMap(block => block.images);

    imagesToValidate.forEach(img => {
        if (!img.validityFrom) {
          hasError = true;
          message = 'Please set validity date for all selected images';
        }
    });

    if (hasError) {
      alert(message);
      return;
    }

    if (isEditMode && uploadableImages.length === 0) {
      setActiveTab("contact");
      return;
    }

    const hasImages = blocks.some(block => block.images.length > 0);
    if (!hasImages || uploadableImages.length === 0) {
      alert('Please upload at least one image to any block');
      return;
    }

    const formatDateTime = (dateStr) => {
      return dateStr ? `${dateStr} 00:00:00` : '';
    };

    const formdata = new FormData();
    formdata.append('destinationUuid', destinationUuid);

    // Each image needs a unique index across all blocks
    let imageIndex = 0;
    blocks.forEach((block) => {
      if (block.images.length > 0) {
        let displayOrder = 1;
        
        block.images.forEach((image) => {
          if (!image.file) {
            return;
          }
          formdata.append(`images[${imageIndex}][clientAppTypeCode]`, "WEB");
          formdata.append(`images[${imageIndex}][displayOrder]`, displayOrder);
          formdata.append(`images[${imageIndex}][imageFile]`, image.file);
          formdata.append(`images[${imageIndex}][validFrom]`, formatDateTime(image.validityFrom));
          formdata.append(`images[${imageIndex}][aspDivWidth]`, block.aspectWidth);
          formdata.append(`images[${imageIndex}][aspDivHeight]`, block.aspectHeight);
          
          displayOrder++;
          imageIndex++;
        });
      }
    });

    console.log('FormData contents:');
    for (let [key, value] of formdata.entries()) {
      console.log(`${key}:`, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
    }

    try {
      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/destination-panel/registration/destination-images',
        formdata,
        {
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        }
      );

      console.log("Full Response:", response);
      console.log("Response Status:", response?.status);
      console.log("Response Data:", response?.data);

      if (response?.data?.status === true || response?.data?.result) {
        console.log("Form submitted successfully:", response.data.result);
        alert('All images saved successfully!');
        setActiveTab("contact");
      } else {
        console.warn("Success response but unexpected format:", response?.data);
        alert('Data may have been saved. Please verify.');
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      console.error("Error response:", error?.response?.data);
      console.error("Error status:", error?.response?.status);
      console.error("Error message:", error?.message);
      
      let errorMessage = 'Error saving data. ';
      
      if (error?.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 413) {
          errorMessage = 'Images are too large. Please reduce image file sizes and try again.';
        } else if (status === 400) {
          // Try to extract meaningful error message
          let detailMessage = 'Please check your inputs';
          
          if (typeof data === 'string') {
            detailMessage = data;
          } else if (data?.message) {
            detailMessage = data.message;
          } else if (data?.error) {
            detailMessage = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
          } else if (data) {
            detailMessage = JSON.stringify(data);
          }
          
          errorMessage = `Invalid data: ${detailMessage}`;
        } else if (status === 401) {
          errorMessage = 'Authentication error. Please login again.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later or contact support.';
        } else {
          let detailMessage = 'Please try again.';
          
          if (typeof data === 'string') {
            detailMessage = data;
          } else if (data?.message) {
            detailMessage = data.message;
          } else if (data?.error) {
            detailMessage = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
          } else if (data) {
            detailMessage = JSON.stringify(data);
          }
          
          errorMessage += `Server error (${status}). ${detailMessage}`;
        }
      } else if (error?.request) {
        errorMessage = 'No response from server. Please check your internet connection and try again.';
      } else {
        errorMessage += error?.message || 'Unknown error occurred. Please try again.';
      }
      
      alert(errorMessage);
    }
  };

  const handleDragStart = (blockId, position) => {
    dragItem.current = { blockId, position };
  };

  const handleDragEnter = (blockId, position) => {
    dragOverItem.current = { blockId, position };
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null) {
      const { blockId: sourceBlockId, position: sourcePosition } = dragItem.current;
      const { blockId: targetBlockId, position: targetPosition } = dragOverItem.current;

      const sourceBlock = blocks.find(block => block.id === sourceBlockId);

      if (sourceBlock) {
        const newBlocks = [...blocks];
        const draggedItem = sourceBlock.images[sourcePosition];

        const updatedSourceBlock = newBlocks.find(block => block.id === sourceBlockId);
        updatedSourceBlock.images = updatedSourceBlock.images.filter((_, index) => index !== sourcePosition);

        const updatedTargetBlock = newBlocks.find(block => block.id === targetBlockId);
        updatedTargetBlock.images.splice(targetPosition, 0, draggedItem);

        dragItem.current = null;
        dragOverItem.current = null;

        setBlocks(newBlocks);
      }
    }
  };

  return (
    <div className="destination-page">
      <div className="content">
        {activeTab === 'images' && (
          !destinationUuid ? (
            <div className="p-6">
              <div className="p-6 text-center rounded bg-blue-50">
                <h3 className="mb-3 text-base font-medium text-gray-700">Destination Images</h3>
                <p className="mb-2 text-sm text-gray-600">Please complete the Basic Info step first to enable image management.</p>
                <p className="mt-3 text-xs text-gray-600">After saving the destination, you will be able to upload images with:</p>
                <p className="mt-2 text-xs text-gray-600">
                  • Multiple image blocks with aspect ratios • Image validity dates • Drag and drop support • Preview functionality
                </p>
              </div>
            </div>
          ) : (
          <div className="p-6 images-content">
            <h2 className="mb-4 text-base font-medium text-slate-700">Destination Images</h2>

            {/* All Blocks with Images */}
            <div className="blocks-container">
              {blocks.map(block => (
                <div key={block.id} className="block-section">
                  <div className="block-header">
                    <h3 className="block-title">{block.name}</h3>
                    <span className="aspect-ratio-badge">
                      Aspect Ratio: {block.aspectWidth}:{block.aspectHeight} 
                      <span className="ml-2 text-xs text-gray-500">({block.exampleResolution})</span>
                    </span>
                  </div>

                  {/* Upload button for this block */}
                  {!isReadOnly && (
                    <div className="mb-4">
                      <input
                        type="file"
                        ref={(el) => (fileInputRefs.current[block.id] = el)}
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        onChange={(e) => handleFileChange(e, block.id)}
                        className="hidden"
                      />
                      <button
                        onClick={() => handleUploadClick(block.id)}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        + Upload Images to {block.name}
                      </button>
                      <p className="mt-2 text-xs text-gray-500">
                        Required: {block.aspectWidth}:{block.aspectHeight} | Examples: {block.exampleResolution} | JPG, PNG or WebP (max 5MB)
                      </p>
                    </div>
                  )}
                  
                  {block.images.length > 0 ? (
                    <div className="block-images">
                      <div className="image-grid">
                        {block.images.map((image, index) => (
                          <div 
                            className="image-item" 
                            key={image.id}
                            draggable={!isReadOnly}
                            onDragStart={!isReadOnly ? () => handleDragStart(block.id, index) : undefined}
                            onDragEnter={!isReadOnly ? () => handleDragEnter(block.id, index) : undefined}
                            onDragEnd={!isReadOnly ? handleDragEnd : undefined}
                            onDragOver={!isReadOnly ? (e) => e.preventDefault() : undefined}
                          >
                            <div className="image-preview">
                              <img 
                                src={image.imageUrl || (image.file ? URL.createObjectURL(image.file) : "/image-placeholder.png")} 
                                alt={image.name} 
                                className="image-preview-img" 
                              />
                            </div>
                            {/* <div className="mb-2 text-xs truncate image-name">{image.name}</div> */}

                            {/* Per-image validity date */}
                            <div className="mt-5 mb-2 image-validity">
                              {/* Valid From Date */}
                              <div className="from-date">
                                <label className="block mb-1 text-xs text-gray-600 validity-label">
                                  Valid From
                                </label>
                                <input
                                  type="date"
                                  value={image.validityFrom}
                                  onChange={(e) => handleImageValidityChange(block.id, image.id, 'validityFrom', e.target.value)}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded validity-input focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  required
                                  disabled={isReadOnly}
                                />
                              </div>
                            </div>
                            
                            {!isReadOnly && (
                              <button 
                                className="delete-button"
                                onClick={() => handleImageDelete(block.id, image.id)}
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="no-images">
                      No images uploaded to this block yet
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          )
        )}
      </div>
      
      <div className="footer">
        <button 
          className="previous-button"
          onClick={handlePreviousClick}
          disabled={isReadOnly}
        >
          Previous
        </button>
        {!isReadOnly && (
          <button 
            className="save-button"
            onClick={handleSaveClick}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default DestinationImage;
